import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Button, Badge, Form, InputGroup } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCalendarAlt, faUser, faTag, faSearch } from '@fortawesome/free-solid-svg-icons';
import './KnowledgeCenter.scss';

/**
 * KnowledgeCenter component - Displays knowledge articles from the articles.json file
 * 
 * @returns {JSX.Element} The KnowledgeCenter component
 */
const KnowledgeCenter = () => {
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredArticles, setFilteredArticles] = useState([]);

  // Load articles from JSON file
  useEffect(() => {
    const fetchArticles = async () => {
      try {
        // Fetch the articles JSON file from public folder
        const articlesPath = `${process.env.PUBLIC_URL}/knowledge-center/articles.json`;
        const articlesResponse = await fetch(articlesPath);
        
        if (!articlesResponse.ok) {
          throw new Error(`Failed to fetch articles: ${articlesResponse.status} ${articlesResponse.statusText}`);
        }
        
        const articlesData = await articlesResponse.json();
        setArticles(articlesData.articles);
        setFilteredArticles(articlesData.articles);
        setLoading(false);
      } catch (err) {
        console.error("Failed to load articles:", err);
        setError("Failed to load knowledge center articles. Please try again later.");
        setLoading(false);
      }
    };

    fetchArticles();
  }, []);
  
  // Filter articles whenever search term changes
  useEffect(() => {
    if (!searchTerm.trim()) {
      setFilteredArticles(articles);
      return;
    }
    
    const term = searchTerm.toLowerCase().trim();
    const filtered = articles.filter(article => 
      article.title.toLowerCase().includes(term) || 
      article.description.toLowerCase().includes(term) || 
      article.tags.some(tag => tag.toLowerCase().includes(term)) ||
      article.author.toLowerCase().includes(term)
    );
    
    setFilteredArticles(filtered);
  }, [searchTerm, articles]);

  // Format date to display in a readable format
  const formatDate = (dateString) => {
    const options = { year: 'numeric', month: 'long', day: 'numeric' };
    return new Date(dateString).toLocaleDateString(undefined, options);
  };
  
  // Handle search input change
  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };

  if (loading) {
    return (
      <Container className="my-5 text-center">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </Container>
    );
  }

  if (error) {
    return (
      <Container className="my-5">
        <div className="alert alert-danger" role="alert">
          {error}
        </div>
      </Container>
    );
  }

  return (
    <section className="knowledge-center-section">
      <Container>
        <h2 className="section-title text-center mb-4">Knowledge Center</h2>
        <p className="section-description text-center mb-5">
          Access our collection of technical resources, guides, and best practices designed to help you
          maximize the benefits of MET Connect® solutions.
        </p>
        
        <div className="search-container mb-5">
          <Row className="justify-content-center">
            <Col md={8} lg={6}>
              <InputGroup className="knowledge-search">
                <Form.Control
                  placeholder="Search for articles, topics, or tags..."
                  value={searchTerm}
                  onChange={handleSearchChange}
                  className="search-input"
                  aria-label="Search knowledge articles"
                />
                <InputGroup.Text className="search-icon">
                  <FontAwesomeIcon icon={faSearch} />
                </InputGroup.Text>
              </InputGroup>
              {searchTerm && (
                <div className="search-results-info text-center mt-2">
                  Found {filteredArticles.length} {filteredArticles.length === 1 ? 'result' : 'results'} for "{searchTerm}"
                  {filteredArticles.length === 0 && (
                    <Button variant="link" onClick={() => setSearchTerm('')}>
                      Clear search
                    </Button>
                  )}
                </div>
              )}
            </Col>
          </Row>
        </div>
        
        <Row>
          {filteredArticles.length === 0 && searchTerm && (
            <Col xs={12} className="text-center no-results">
              <div className="no-results-message">
                <h4>No articles found</h4>
                <p>Try different keywords or browse all articles by clearing the search</p>
                <Button variant="primary" onClick={() => setSearchTerm('')}>
                  View All Articles
                </Button>
              </div>
            </Col>
          )}
          {filteredArticles.map((article) => (
            <Col md={6} lg={4} key={article.id} className="mb-4">
              <Card className="knowledge-card h-100">
                <div className="card-image-container">
                  <img 
                    src={process.env.PUBLIC_URL + article.image} 
                    alt={article.title} 
                    className="card-img-top"
                  />
                </div>
                <Card.Body>
                  <Card.Title>{article.title}</Card.Title>
                  <Card.Text>{article.description}</Card.Text>
                  
                  <div className="article-meta">
                    <div className="meta-item">
                      <FontAwesomeIcon icon={faCalendarAlt} className="meta-icon" />
                      <span>{formatDate(article.datePublished)}</span>
                    </div>
                    <div className="meta-item">
                      <FontAwesomeIcon icon={faUser} className="meta-icon" />
                      <span>{article.author}</span>
                    </div>
                  </div>
                  
                  <div className="article-tags mb-3">
                    {article.tags.map((tag, index) => (
                      <Badge bg="light" text="dark" key={index} className="me-2">
                        <FontAwesomeIcon icon={faTag} className="me-1" />
                        {tag}
                      </Badge>
                    ))}
                  </div>
                </Card.Body>
                <Link to={`/knowledge/${article.id}`} className="knowledge-card-footer">
                  Read Article
                </Link>
              </Card>
            </Col>
          ))}
        </Row>
      </Container>
    </section>
  );
};

export default KnowledgeCenter;
